import React, { forwardRef } from 'react';
import proptypes from 'prop-types';
import {
  FormController,
  FormLabel,
  Dropdown,
  TextField
} from '@one-thd/sui-atomic-components';

const {
  bool, func, node, string
} = proptypes;

// Shared field wrappers for the project/delivery forms.
//
// MOST fields in these forms repeat the same pair of conditional props:
//   status={showErrors && errors.someError ? 'error' : null}
//   statusMessage={showErrors ? errors.someError : ''}
// Repeated once per field, that pair is what drives the forms' Cognitive
// Complexity findings (Sonar S3776) - the complexity is repetition width, not
// branching depth. Collapsing the pair in here leaves each caller with a single
// `error` + `showErrors` prop and no conditional logic of its own.
//
// These wrappers only fit fields that follow that showErrors-gated pattern. Fields
// that do not are deliberately built separately in ProjectInformationSections.jsx
// rather than forced through here:
//   - Job Site Hours start/end show their error as soon as it is set, with no
//     showErrors gate, so routing them through here would suppress errors that are
//     currently visible.
//   - The Project Type dropdown displays no validation status at all (only the
//     free-text field it reveals does), and its test id is unsuffixed.
//
// ValidatedTextField's forwardRef is load-bearing: the forms attach refs to text
// fields (nameRef, phoneRef, ...) and call scrollIntoView on the first field in
// error, and a plain function component would swallow the ref with only a console
// warning. ValidatedDropdown is forwardRef for consistency, but note it cannot
// actually deliver a usable ref today - sui's Dropdown is not itself a forwardRef
// component, so any ref aimed at it stays null and the scroll-to-error behavior
// silently skips dropdown fields. That is pre-existing, not introduced here, and is
// pinned by a test in ValidatedFields.spec.jsx so it surfaces if sui changes.

// `hidden` lets a caller keep its field list flat and unconditional: the forms gate
// many fields on hideProjectInfo/hideDeliveryInfo, and leaving those as `{!hideX && (
// ... )}` at the call site is itself a per-field conditional, which is the thing the
// Cognitive Complexity finding is counting. Returning null in here renders exactly the
// same nothing.
export const ValidatedTextField = forwardRef(({
  id,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  showErrors,
  testId,
  optional,
  required,
  hidden
}, ref) => {
  if (hidden) {
    return null;
  }
  return (
    <TextField
      fullWidth
      ref={ref}
      optional={optional}
      required={required}
      id={id}
      label={label}
      placeholder={placeholder}
      value={value || ''}
      onBlur={onBlur}
      onChange={onChange}
      status={showErrors && error ? 'error' : null}
      statusMessage={showErrors ? error : ''}
      InputProps={{
        inputProps: {
          'data-testid': testId
        }
      }}
    />
  );
});

ValidatedTextField.displayName = 'ValidatedTextField';

ValidatedTextField.propTypes = {
  id: string.isRequired,
  label: string.isRequired,
  testId: string.isRequired,
  onChange: func.isRequired,
  onBlur: func.isRequired,
  placeholder: string,
  value: string,
  error: string,
  showErrors: bool,
  optional: bool,
  required: bool,
  hidden: bool
};

ValidatedTextField.defaultProps = {
  placeholder: '',
  value: '',
  error: '',
  showErrors: false,
  optional: false,
  required: false,
  hidden: false
};

// `value` is passed straight through rather than normalized in here: the callers
// deliberately use different fallbacks (`?? null` vs `|| ''` vs a display-value
// mapper) and those are not interchangeable, so normalizing centrally would
// change behavior. MenuItems stay as children so each field keeps its own option
// list and test ids visible at the call site.
export const ValidatedDropdown = forwardRef(({
  id,
  label,
  testId,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  showErrors,
  hidden,
  children
}, ref) => {
  if (hidden) {
    return null;
  }
  return (
    <FormController fullWidth>
      <FormLabel
        optional
        htmlFor={id}
        id={`${id}-label`}
      >
        {label}
      </FormLabel>
      <Dropdown
        ref={ref}
        id={id}
        data-testid={testId}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        placeholder={placeholder}
        status={showErrors && error ? 'error' : null}
        statusMessage={showErrors ? error : ''}
      >
        {children}
      </Dropdown>
    </FormController>
  );
});

ValidatedDropdown.displayName = 'ValidatedDropdown';

ValidatedDropdown.propTypes = {
  id: string.isRequired,
  label: string.isRequired,
  testId: string.isRequired,
  children: node.isRequired,
  onChange: func.isRequired,
  onBlur: func.isRequired,
  value: string,
  placeholder: string,
  error: string,
  showErrors: bool,
  hidden: bool
};

ValidatedDropdown.defaultProps = {
  value: null,
  placeholder: '',
  error: '',
  showErrors: false,
  hidden: false
};
